import { FrameContexts } from "@microsoft/teams-js";
import { report } from "@aglocal/web/error-reporting";
import { safeJsonStringify } from "../../helpers/safeJsonStringify";
import useAsync from "@aglocal/web/hooks/useAsync";
import { useMissionControl } from "@aglocal/web/mission-control";

import useIId from "@/hooks/useIId";
import { useTeams } from "@/teams";

import ErrorPage from "@/pages/Error";
import LoadingPage from "@/pages/Loading";

import FullPageApp from "./FullPageApp";
import SidePanelTab from "./SidePanelTab";
import { useEffect } from "react";

function _contextToRecord(context: unknown): Record<string, unknown> {
  if (context instanceof Error) {
    return {
      message: context.message,
      stack: context.stack,
      name: context.name,
    };
  }
  if (typeof context !== "object" || context === null) return {};
  return Object.fromEntries(Object.entries(context));
}

function useIIDFromMeeting() {
  const requestedIID = useIId();
  const api = useMissionControl();
  const teams = useTeams();

  const meetingId = teams.inTeams ? teams.context.chat?.id : undefined;

  useEffect(() => {
    if (teams.inTeams && !meetingId) {
      void api.logEvent(
        "",
        "TeamsClient - Meeting Id missing from Side Panel",
        _contextToRecord(teams.context),
      );
      console.error(teams);
      void report(
        `Side Panel - Meeting Id not in context. \n ${safeJsonStringify(teams)}`,
      );
    }
  }, [teams, meetingId, api]);

  return useAsync(async () => {
    // Use iid from the URL first
    if (requestedIID !== null) {
      return requestedIID;
    }

    // Check if we are still loading, or that we haven't gotten a meeting ID from the teams context
    if (teams.loading || !teams.inTeams || !meetingId) {
      return null;
    }

    try {
      const iid = await api.getInterviewIdByMeetingId(meetingId);
      return iid.id;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      const msg = `${errorMsg} Side Panel - \n ${safeJsonStringify(teams)}`;
      const newError = new Error(msg);
      console.error(newError);
      void report(newError);
      return null;
    }
  }, [teams, requestedIID, meetingId, api]);
}

export default function TalentAssistantApp() {
  const teams = useTeams();
  const iid = useIIDFromMeeting();

  if (teams.loading || iid.loading) {
    return <LoadingPage />;
  }

  if (iid.result == null) {
    return <ErrorPage title="No interview Id provided" />;
  }

  switch (teams.inTeams && teams.context.page.frameContext) {
    case FrameContexts.sidePanel:
      return <SidePanelTab iid={iid.result} />;
    default:
      return <FullPageApp iid={iid.result} />;
  }
}
